import { useEffect, useId, type ReactNode } from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";
import { X } from "lucide-react";

import styles from "./modal.module.css";

let bodyScrollLockCount = 0;
let bodyOverflowBeforeFirstModal = "";

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children?: ReactNode;
  footer?: ReactNode;
  size?: "sm" | "md" | "lg" | "xl";
  minHeight?: string | number;
  className?: string;
  /** Optional class for the full-screen backdrop/portal layer. */
  backdropClassName?: string;
  /** Optional class for the scrollable body region (e.g. `overflow: hidden` for nested scroll). */
  bodyClassName?: string;
  /** Optional class for the header row (e.g. tighter padding below the title). */
  headerClassName?: string;
  /** When true, clicking the backdrop calls `onClose`. Default false; pass true to opt back in to backdrop dismiss. */
  closeOnBackdropClick?: boolean;
  /** When true, Escape calls `onClose`. Default false; pass true to opt back in to Escape dismiss. */
  closeOnEscape?: boolean;
  /** When false, the header close control is not rendered (e.g. required gate before continuing). */
  hideCloseButton?: boolean;
}

export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  size = "md",
  minHeight,
  className,
  backdropClassName,
  bodyClassName,
  headerClassName,
  closeOnBackdropClick = false,
  closeOnEscape = false,
  hideCloseButton = false,
}: ModalProps) {
  const titleId = useId();

  useEffect(() => {
    if (!open) return;
    if (bodyScrollLockCount === 0) bodyOverflowBeforeFirstModal = document.body.style.overflow;
    bodyScrollLockCount += 1;
    document.body.style.overflow = "hidden";
    return () => {
      bodyScrollLockCount = Math.max(0, bodyScrollLockCount - 1);
      if (bodyScrollLockCount === 0) document.body.style.overflow = bodyOverflowBeforeFirstModal;
    };
  }, [open]);

  useEffect(() => {
    if (!open || !closeOnEscape) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose, closeOnEscape]);

  if (!open) return null;

  return createPortal(
    <div
      className={clsx(styles.backdrop, backdropClassName)}
      role="presentation"
      onClick={closeOnBackdropClick ? onClose : undefined}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={clsx(
          styles.panel,
          size === "sm" && styles.panelSm,
          size === "lg" && styles.panelLg,
          size === "xl" && styles.panelXl,
          className,
        )}
        style={{ minHeight }}
        onClick={(event) => event.stopPropagation()}
      >
        <header className={clsx(styles.header, headerClassName)}>
          <h2 id={titleId} className={clsx("heading-lg", styles.title)}>
            {title}
          </h2>
          {hideCloseButton ? null : (
            <button
              type="button"
              className={styles.close}
              aria-label="Close"
              onClick={onClose}
            >
              <X size={20} strokeWidth={2} aria-hidden />
            </button>
          )}
        </header>
        <div className={clsx(styles.body, bodyClassName)}>{children}</div>
        {footer != null ? <footer className={styles.footer}>{footer}</footer> : null}
      </div>
    </div>,
    document.body,
  );
}
